import { z } from "zod";
import { isoDateTimeString, paginationQuerySchema, uuidSchema } from "./common";
import { applicationSourceSchema } from "./applications";

// Mirror of Prisma enum InviteStatus.
export const inviteStatusSchema = z.enum([
  "LOCKED",
  "ACCEPTED",
  "COMMITTED",
  "REJECTED",
  "EXPIRED",
  "CANCELLED",
]);
export type InviteStatusValue = z.infer<typeof inviteStatusSchema>;

// ----------------------------------------------------------------------------
// Slot
// ----------------------------------------------------------------------------
// An interview slot lasts SLOT_DURATION_MINUTES, but its start time only needs
// to align to SLOT_STEP_MINUTES. Keeping these separate lets the picker offer
// :00/:15/:30/:45 starts while each booking still spans 30 minutes.
// Shared so the API (alignment validation) and the web time picker (`step` attr)
// stay in lockstep — change the step here and both ends follow.
export const SLOT_DURATION_MINUTES = 30;
export const SLOT_STEP_MINUTES = 15;
/** `step` value (in seconds) for an `<input type="time">` start-time picker. */
export const SLOT_STEP_SECONDS = SLOT_STEP_MINUTES * 60;

// Pickable interview-hours window (F&B: morning prep to late-night close).
// Shared by the slot-creation time picker (min/max) and the calendar grid
// band so both stay in lockstep.
export const INTERVIEW_DAY_START_HOUR = 6;
export const INTERVIEW_DAY_END_HOUR = 22;
/** "HH:00" strings for `<input type="time">` min/max attributes. */
export const INTERVIEW_DAY_START_TIME = `${String(INTERVIEW_DAY_START_HOUR).padStart(2, "0")}:00`;
export const INTERVIEW_DAY_END_TIME = `${String(INTERVIEW_DAY_END_HOUR).padStart(2, "0")}:00`;

export const interviewSlotSchema = z.object({
  id: uuidSchema,
  orgAddressId: uuidSchema,
  startAt: isoDateTimeString,
  endAt: isoDateTimeString,
  capacityTotal: z.number().int().min(1).max(5),
  capacityRemaining: z.number().int().min(0),
  createdAt: isoDateTimeString,
});
export type InterviewSlot = z.infer<typeof interviewSlotSchema>;

export const createSlotRequestSchema = z.object({
  orgAddressId: uuidSchema,
  startAt: isoDateTimeString,
  capacityTotal: z.number().int().min(1).max(5).default(1),
});
export type CreateSlotRequest = z.infer<typeof createSlotRequestSchema>;

export const slotListResponseSchema = z.object({
  items: z.array(interviewSlotSchema),
});
export type SlotListResponse = z.infer<typeof slotListResponseSchema>;

// ----------------------------------------------------------------------------
// Invite
// ----------------------------------------------------------------------------
export const inviteContactSchema = z.object({
  fullName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  email: z.string().nullable(),
  zalo: z.string().nullable(),
  facebook: z.string().nullable(),
  instagram: z.string().nullable(),
});
export type InviteContact = z.infer<typeof inviteContactSchema>;

export const inviteSchema = z.object({
  id: uuidSchema,
  applicationId: uuidSchema,
  postingId: uuidSchema,
  postingTitle: z.string(),
  // Worker-initiated apply vs employer-sourced ("Tìm ứng viên") — drives the
  // worker-side copy ("Quán X đã mời bạn" vs "Bạn đã ứng tuyển"), docs/01-product.md.
  source: applicationSourceSchema,
  orgId: uuidSchema,
  orgName: z.string(),
  orgAddressId: uuidSchema,
  orgAddress: z.object({
    alias: z.string(),
    address: z.string(),
    district: z.string(),
    city: z.string(),
    lat: z.number(),
    lng: z.number(),
  }),
  status: inviteStatusSchema,
  acceptedSlot: interviewSlotSchema.nullable(),
  acceptedAt: isoDateTimeString.nullable(),
  committedAt: isoDateTimeString.nullable(),
  cancelledAt: isoDateTimeString.nullable(),
  cancellationReason: z.string().nullable(),
  slotOptions: z.array(interviewSlotSchema),
  // Contact shared only when status === COMMITTED, otherwise null.
  contact: inviteContactSchema.nullable(),
  expiresAt: isoDateTimeString,
  createdAt: isoDateTimeString,
});
export type Invite = z.infer<typeof inviteSchema>;

export const createInviteRequestSchema = z.object({
  applicationId: uuidSchema,
  orgAddressId: uuidSchema,
  slotIds: z.array(uuidSchema).min(1).max(10),
});
export type CreateInviteRequest = z.infer<typeof createInviteRequestSchema>;

export const acceptInviteRequestSchema = z.object({
  slotId: uuidSchema,
});
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequestSchema>;

export const requestCancellationRequestSchema = z.object({
  reason: z.string().min(1).max(1000),
});
export type RequestCancellationRequest = z.infer<
  typeof requestCancellationRequestSchema
>;

export const inviteListQuerySchema = paginationQuerySchema;
export type InviteListQuery = z.infer<typeof inviteListQuerySchema>;

// ----------------------------------------------------------------------------
// Employer interview calendar ("Lịch phỏng vấn")
// ----------------------------------------------------------------------------
// Time-centric, cross-posting view of scheduled interviews (ACCEPTED +
// COMMITTED). LOCKED invites have no slot yet so they are surfaced only as a
// `pendingCount` banner. Contact + full name unlock at COMMITTED.
export const interviewCalendarItemSchema = z.object({
  inviteId: uuidSchema,
  applicationId: uuidSchema,
  postingId: uuidSchema,
  postingTitle: z.string(),
  workerMaskedName: z.string(),
  workerFullName: z.string().nullable(),
  workerAvatarUrl: z.string().nullable(),
  committed: z.boolean(),
  startAt: isoDateTimeString,
  endAt: isoDateTimeString,
  orgAddressId: uuidSchema,
  orgAddressAlias: z.string(),
  district: z.string(),
  capacityTotal: z.number().int().min(1),
  contact: inviteContactSchema.nullable(),
});
export type InterviewCalendarItem = z.infer<typeof interviewCalendarItemSchema>;

export const interviewCalendarQuerySchema = z.object({
  from: isoDateTimeString.optional(),
  to: isoDateTimeString.optional(),
  orgAddressId: uuidSchema.optional(),
});
export type InterviewCalendarQuery = z.infer<
  typeof interviewCalendarQuerySchema
>;

export const interviewCalendarResponseSchema = z.object({
  items: z.array(interviewCalendarItemSchema),
  // LOCKED invites awaiting the worker's slot choice (not yet on the grid).
  pendingCount: z.number().int().min(0),
});
export type InterviewCalendarResponse = z.infer<
  typeof interviewCalendarResponseSchema
>;

export const noShowResponseSchema = z.object({
  ok: z.literal(true),
  alreadyReported: z.boolean().optional(),
});
export type NoShowResponse = z.infer<typeof noShowResponseSchema>;

export const inviteListResponseSchema = z.object({
  items: z.array(inviteSchema),
  nextCursor: z.string().nullable(),
});
export type InviteListResponse = z.infer<typeof inviteListResponseSchema>;

export const inviteDetailResponseSchema = z.object({
  invite: inviteSchema,
});
export type InviteDetailResponse = z.infer<typeof inviteDetailResponseSchema>;

// ----------------------------------------------------------------------------
// Cancellation request (admin queue)
// ----------------------------------------------------------------------------
export const cancellationStatusSchema = z.enum([
  "PENDING",
  "APPROVED_REFUND",
  "APPROVED_NO_REFUND",
  "REJECTED",
]);
export type CancellationStatusValue = z.infer<typeof cancellationStatusSchema>;

export const cancellationRequestSchema = z.object({
  id: uuidSchema,
  inviteId: uuidSchema,
  requestedByUserId: uuidSchema,
  requesterRole: z.enum(["WORKER", "EMPLOYER", "ADMIN", "SYSTEM"]),
  reason: z.string(),
  status: cancellationStatusSchema,
  adminNote: z.string().nullable(),
  resolvedAt: isoDateTimeString.nullable(),
  createdAt: isoDateTimeString,
  // Enriched identity (from the linked invite) so the admin queue can show who
  // is involved and link out to org/worker detail pages.
  orgId: uuidSchema.nullable(),
  orgName: z.string().nullable(),
  workerId: uuidSchema.nullable(),
  workerName: z.string().nullable(),
  workerAvatarUrl: z.string().nullable(),
  postingId: uuidSchema.nullable(),
  postingTitle: z.string().nullable(),
});
export type CancellationRequestDto = z.infer<typeof cancellationRequestSchema>;

export const resolveCancellationRequestSchema = z.object({
  resolution: z.enum(["APPROVED_REFUND", "APPROVED_NO_REFUND", "REJECTED"]),
  adminNote: z.string().max(1000).optional(),
});
export type ResolveCancellationRequest = z.infer<
  typeof resolveCancellationRequestSchema
>;

export const cancellationRequestListResponseSchema = z.object({
  items: z.array(cancellationRequestSchema),
  nextCursor: z.string().nullable(),
});
export type CancellationRequestListResponse = z.infer<
  typeof cancellationRequestListResponseSchema
>;
